/**
 * Referral rewards.
 *
 * Model (no new tables — stored on the existing user_registrations rows):
 *   Referred agency  data.referredBy        = referrer uid (from ?ref= at signup)
 *                    data.referralRewarded  = true once the reward has been paid
 *                    data.referralRewardedAt = ISO date
 *   Referrer agency  data.subscriptionEnd   extended by `freeDays`
 *                    data.referralRewards[] = log of granted rewards
 *
 * Reward rules:
 *   - Activated ONLY on the referred agency's FIRST subscription payment.
 *   - One reward per referred agency (referralRewarded guard → no duplicates).
 *   - Free days are added to the referrer's CURRENT active subscription end
 *     (or from today if expired).
 */

const REFERRAL_CONFIG_ID = 'referral_config';
export const DEFAULT_FREE_DAYS = 15;
export const DEFAULT_CASH = 200;
export const DEFAULT_CURRENCY = 'DH';

export const getReferralConfig = async (supabase) => {
  try {
    const { data } = await supabase.from('sa_settings').select('data').eq('id', REFERRAL_CONFIG_ID).single();
    return {
      freeDays: Number(data?.data?.freeDays) || DEFAULT_FREE_DAYS,
      cashAmount: Number(data?.data?.cashAmount) || DEFAULT_CASH,
      currency: data?.data?.currency || DEFAULT_CURRENCY,
    };
  } catch (_) {
    return { freeDays: DEFAULT_FREE_DAYS, cashAmount: DEFAULT_CASH, currency: DEFAULT_CURRENCY };
  }
};

export const saveReferralConfig = async (supabase, { freeDays, cashAmount, currency }) => {
  await supabase.from('sa_settings').upsert({
    id: REFERRAL_CONFIG_ID,
    data: {
      freeDays: Math.max(0, parseInt(freeDays, 10) || 0),
      cashAmount: Math.max(0, parseInt(cashAmount, 10) || 0),
      currency: currency || DEFAULT_CURRENCY,
      updatedAt: new Date().toISOString(),
    },
  });
};

const addDays = (base, days) => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * On the referred agency's first payment, create a PENDING reward credit for the
 * referrer. The referrer later CHOOSES how to redeem it (free days OR cash) from
 * their Parrainage page. Idempotent (one credit per referred agency).
 */
export const grantReferralReward = async ({ supabase, referredAccount }) => {
  if (!referredAccount) return { granted: false, reason: 'no_account' };
  const referrerUid = referredAccount.referredBy || referredAccount.data?.referredBy;
  if (!referrerUid) return { granted: false, reason: 'not_referred' };
  if (referredAccount.referralRewarded || referredAccount.data?.referralRewarded) {
    return { granted: false, reason: 'already_rewarded' };
  }

  const cfg = await getReferralConfig(supabase);
  if (!cfg.freeDays && !cfg.cashAmount) return { granted: false, reason: 'no_reward_configured' };

  const { data: refRow } = await supabase
    .from('user_registrations').select('data').eq('uid', referrerUid).single();
  if (!refRow) return { granted: false, reason: 'referrer_not_found' };

  const refData = refRow.data || {};
  const reward = {
    referredUid: referredAccount.uid,
    referredName: referredAccount.agencyName || referredAccount.name || referredAccount.email || referredAccount.uid,
    freeDays: cfg.freeDays,
    cashAmount: cfg.cashAmount,
    currency: cfg.currency,
    grantedAt: new Date().toISOString(),
    redeemed: false,           // referrer chooses days OR cash later
    redeemType: null,
  };

  await supabase.from('user_registrations').update({
    data: {
      ...refData,
      referralRewards: [...(refData.referralRewards || []), reward],
    },
  }).eq('uid', referrerUid);

  // Mark the referred agency as rewarded (prevents duplicate credits).
  const { data: rdRow } = await supabase
    .from('user_registrations').select('data').eq('uid', referredAccount.uid).single();
  await supabase.from('user_registrations').update({
    data: { ...(rdRow?.data || {}), referralRewarded: true, referralRewardedAt: new Date().toISOString() },
  }).eq('uid', referredAccount.uid);

  return { granted: true, freeDays: cfg.freeDays, cashAmount: cfg.cashAmount, referrerUid };
};

/**
 * Redeem a pending reward credit (called by the REFERRER on their own row).
 *   type 'days' → extends own subscription by reward.freeDays
 *   type 'cash' → creates a pending withdrawal request for reward.cashAmount
 */
export const redeemReward = async ({ supabase, uid, referredUid, type }) => {
  const { data: row } = await supabase.from('user_registrations').select('data').eq('uid', uid).single();
  const d = row?.data || {};
  const rewards = d.referralRewards || [];
  const idx = rewards.findIndex(r => r.referredUid === referredUid && !r.redeemed);
  if (idx < 0) return { ok: false, reason: 'not_found' };
  const rw = rewards[idx];
  const patch = { ...d };

  if (type === 'days') {
    const base = (d.subscriptionEnd && new Date(d.subscriptionEnd) > new Date())
      ? new Date(d.subscriptionEnd) : new Date();
    const newEnd = addDays(base, rw.freeDays || 0);
    patch.subscriptionEnd = newEnd.toISOString().split('T')[0];
    patch.referralFreeDaysTotal = (d.referralFreeDaysTotal || 0) + (rw.freeDays || 0);
  } else {
    patch.referralCashTotal = (d.referralCashTotal || 0) + (rw.cashAmount || 0);
    patch.referralWithdrawals = [
      ...(d.referralWithdrawals || []),
      { amount: rw.cashAmount, currency: rw.currency || 'DH', referredUid, referredName: rw.referredName, requestedAt: new Date().toISOString(), status: 'pending' },
    ];
  }

  patch.referralRewards = rewards.map((r, i) =>
    i === idx ? { ...r, redeemed: true, redeemType: type, redeemedAt: new Date().toISOString() } : r);

  await supabase.from('user_registrations').update({ data: patch }).eq('uid', uid);
  return { ok: true, type };
};

/** Short referral code = first uid segment (8 hex chars). Resolvable by prefix. */
export const shortCode = (uid) => String(uid || '').split('-')[0];

export const referralLink = (uid, base) => {
  const origin = base || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/?ref=${shortCode(uid)}`;
};

export const buildReferralRows = (accounts = []) => {
  const byUid = {};
  accounts.forEach(a => { byUid[a.uid] = a; });
  return accounts
    .filter(a => a.referredBy)
    .map(a => {
      const referrer = byUid[a.referredBy];
      return {
        referredUid: a.uid,
        referredName: a.agencyName || a.name || a.email || a.uid,
        referrerUid: a.referredBy,
        referrerName: referrer ? (referrer.agencyName || referrer.name || referrer.email || referrer.uid) : a.referredBy,
        status: a.referralRewarded ? 'activated' : 'pending',
        date: a.referralRewardedAt || a.createdAt || null,
        hasPaid: Array.isArray(a.payments) && a.payments.length > 0,
      };
    });
};
